import * as React from 'react';
import { StyleSheet, css } from 'aphrodite/no-important';
import { CardCaptionContainer, CARD_CAPTION_CONTAINER_TYPES } from '../../CardCaptionContainer';
import { DecorationLine } from '../../DecorationLine';
import { ConditionalVideoWrap } from '../../ConditionalVideoWrap';
import { WrapperForCard } from '../WrapperForCard';
import { MEDIA_BREAKPOINTS } from '../../../../mediaQueries.const';
import { Image } from '../../image/Image';
import { Link } from '../../link/Link';
import { createStyleForBoxShadow } from '../../../../theming/boxShadow';
import { H3, semanticCSS } from '../../../../typography/semanticTags';
import { BaseUnit } from '../../../../theming/baseUnitDefinition';
import { CardComponentDataProps } from '../cards.utils';

export enum DIVIDED_CARD_TYPES {
  HORIZONTAL = 'HORIZONTAL',
  VERTICAL = 'VERTICAL',
}

interface DividedCardComponentThemeProps {
  cardBackgroundColor: string
  cardTextColorTypeNormal: string
  cardTextColorTypeHover: string
  cardTextColorTypeActive: string
  baseUnit?: BaseUnit | null
  borderRadius: number
  boxShadowTypeNormal: string
  boxShadowTypeActive: string
  boxShadowTypeHover: string
  borderWidthTypeNormal: number
  borderWidthTypeHover: number
  borderWidthTypeActive: number
  borderColorTypeNormal: string
  borderColorTypeHover: string
  borderColorTypeActive: string
}

interface Orientation {
  dividedCardType: 'HORIZONTAL' | 'VERTICAL'
}

type Props = CardComponentDataProps & DividedCardComponentThemeProps & Orientation;

const getHorizontalCardSmallScreenHeight = (baseUnit: BaseUnit | null) => {
  return baseUnit
    ? {
      height: `${baseUnit * 4.5}px`,
    }
    : '';
};

const createCardStyles = (cardBackgroundColor: string, cardTextColorTypeNormal:string, cardTextColorTypeHover:string, cardTextColorTypeActive:string, baseUnit: BaseUnit | null, borderRadius: number) => {
  return StyleSheet.create({
    text: {
      overflow: 'hidden',
    },
    dividedCard: {
      width: '100%',
      height: '100%',
      textDecoration: 'none',
      color: cardTextColorTypeNormal,
      backgroundColor: cardBackgroundColor,
      '-webkit-mask-image': '-webkit-radial-gradient(white, black)',
      borderRadius: `${borderRadius}px`,
      position: 'relative',
      ':hover': {
        color: cardTextColorTypeHover,
      },
      ':active': {
        color: cardTextColorTypeActive,
      },
    },
    [DIVIDED_CARD_TYPES.HORIZONTAL]: {
      display: 'grid',
      gridTemplateColumns: '1fr 2fr',
      [MEDIA_BREAKPOINTS.small]: getHorizontalCardSmallScreenHeight(baseUnit),
    },
    [DIVIDED_CARD_TYPES.VERTICAL]: {
      display: 'inline-block',
    },
  });
};

export const DividedCardComponent: React.FunctionComponent<Props> = props => {
  const {
    title,
    image,
    articleUrl,
    coverType,
    dividedCardType,
    numberOfLines = 2,
    cardBackgroundColor,
    cardTextColorTypeNormal,
    cardTextColorTypeHover,
    cardTextColorTypeActive,
    showDecorationLine = false,
    baseUnit = null,
    boxShadowTypeNormal,
    boxShadowTypeActive,
    boxShadowTypeHover,
    borderRadius,
    borderColorTypeActive,
    borderColorTypeHover,
    borderColorTypeNormal,
    borderWidthTypeActive,
    borderWidthTypeHover,
    borderWidthTypeNormal } = props;
  const cardStyles = createCardStyles(cardBackgroundColor, cardTextColorTypeNormal, cardTextColorTypeHover, cardTextColorTypeActive, baseUnit, borderRadius);
  const borderBoxShadow = {
    borderColorTypeActive,
    borderColorTypeHover,
    borderColorTypeNormal,
    borderWidthTypeActive,
    borderWidthTypeHover,
    borderWidthTypeNormal,
  };
  const boxShadowStyle = createStyleForBoxShadow({ boxShadowTypeNormal, boxShadowTypeHover, boxShadowTypeActive, borderBoxShadow });

  return (
    <WrapperForCard
      boxShadowStyle={boxShadowStyle}
      borderRadius={borderRadius}
    >
      <Link
        className={css(
          cardStyles.dividedCard,
          cardStyles[dividedCardType],
          boxShadowStyle.style,
        )}
        href={articleUrl}
      >
        <ConditionalVideoWrap shouldWarp={coverType === 'video'}>
          <Image {...image} lazyLoad />
        </ConditionalVideoWrap>
        {showDecorationLine ? <DecorationLine /> : null}
        <CardCaptionContainer numberOfLines={numberOfLines} type={CARD_CAPTION_CONTAINER_TYPES[dividedCardType]}>
          <H3 styles={semanticCSS(cardStyles.text)}>
            {title}
          </H3>
        </CardCaptionContainer>
      </Link>
    </WrapperForCard>

  );
};
